// Typed design-token contract for the theme system.
//
// Terminology:
// - Theme    = a family (e.g. "EVI Default", "Luce")
// - Variant  = the light or dark rendition of a family
// - Mode     = `light` | `dark` — which variant is active
//
// The CSS custom properties below are the runtime payload consumers read.
// `ThemeProvider` paints them on `:root` on every change.

export type ModeId = 'light' | 'dark';

export type ThemeFamilyId =
  | 'default'
  | 'luce'
  | 'cartographic'
  // Driver Themes ticket additions — promoted from skin overlays
  // (neon, obsidian) or net-new (electric-light, dark-electric).
  // Must stay in lockstep with the User.active_theme_family enum
  // at packages/db/src/models/User.ts and the matching
  // PublicUserSchema z.enum extension.
  | 'neon'
  | 'electric-light'
  | 'dark-electric'
  | 'obsidian'
  // Post unlock-model rewrite — milestone-locked theme families.
  // 'aurora' (northern lights — emerald/magenta/teal) and 'cyber'
  // (cyberpunk — magenta/electric purple harmonic waves) ship
  // with their own shader builds. Same lockstep requirement on
  // User.active_theme_family + PublicUserSchema.
  | 'aurora'
  | 'cyber';

export interface ThemeColors {
  bg: string;
  surface: string;
  surface2: string;

  // Sidebar palette. Shared across light/dark variants of the same family
  // on purpose — the admin sidebar should never flip on mode change.
  sidebar: string;
  sidebarText: string;
  sidebarMute: string;
  sidebarActive: string;
  /** Text and background of the active item in the Admin's fixed-dark TopNav dropdown and mobile drawer. Optional: a theme whose accent is
   *  too dark for that panel sets it (Electric Light, 20 October 2026); unset, the nav uses the accent on the default tint. */
  navActive?: string;
  navActiveBg?: string;

  border: string;
  borderStrong: string;

  accent: string;
  accentSoft: string;
  /**
   * Foreground colour for content rendered on top of `accent`. Used
   * by the primary-CTA pattern (StickyCTA button text, accent-filled
   * pills, accent-tinted selection states). Themes where `accent` is
   * a mid- or dark-luminance colour set this to white (#FFFFFF); the
   * greyscale-rework families (Electric Light, Dark Electric) whose
   * accent is high-luminance white/light-grey set this to a dark
   * value matching the variant's `bg` or `text` so the CTA text
   * stays readable.
   *
   * Added post-greyscale-rework: a global `--color-brand-tx`
   * literal (#000000 in apps/web/src/lib/theme.css + apps/admin's
   * sibling file) predates this field and is still used at ~22
   * call sites (including some that use it as a `background` rather
   * than a foreground), so it's intentionally kept at the literal
   * value — not aliased to `--evi-on-accent` — to avoid a global
   * visual regression on those call sites. New call sites that want
   * a theme-aware on-accent foreground should read
   * `var(--evi-on-accent, #FFFFFF)` (or `#000` fallback as
   * appropriate for the local default).
   */
  onAccent: string;

  charging: string;
  chargingGlow: string;

  // Semantic channels (Ferrari-Luce label-pill system).
  power: string;
  info: string;
  warn: string;
  good: string;
  /**
   * "Active in-use" tone — amber/orange. Distinct from `charging`
   * (brand-green on Default, amber on Luce; "energy in motion" used
   * by Power Flow ring, charging bars, animations) because the
   * station detail status grid needs THREE distinct colours for
   * Available / Charging / Unavailable, and reusing the brand-
   * green `charging` token would collapse Available + Charging to
   * the same colour on the Default theme. Stays amber-family on
   * every theme — semantic intent is "this connector is busy
   * right now," not "system delivering energy."
   */
  busy: string;

  mute: string;
  text: string;
  textMute: string;

  /**
   * Full-strength card rim glow. Used on admin dashboard widgets in dark
   * mode. Full CSS `box-shadow` string — `'none'` in light variants.
   */
  cardGlow: string;
  /**
   * Subtle card rim glow — half-step from `cardGlow`. Used on PWA
   * ListRow + marquee cards where full-strength would be noisy. `'none'`
   * in light variants.
   */
  cardGlowSubtle: string;

  // ── Widget chrome (admin dashboard) ─────────────────────────────────
  // The dashboard's `.widget` cards previously hardcoded a dark-glass
  // recipe (rgba(20,20,28,0.7) + blur(16px) saturate(160%) + drop
  // shadow). That worked in dark mode over the satellite map but
  // produced washed-out "haze" in light mode over the light basemap.
  // These four tokens parameterise the recipe so light + dark + the
  // Luce family each get their own surface treatment.
  /**
   * Background colour for dashboard widget cards. Tuned per (theme,
   * mode) — translucent dark in dark, translucent light in light,
   * warm-tinted on the Luce family.
   */
  widgetBg: string;
  /**
   * Hairline border on widget cards. Pairs with `widgetBg` per (theme,
   * mode); dark variants use white-at-low-alpha, light variants use
   * black-at-low-alpha, Luce variants pick a warm-toned border.
   */
  widgetBorder: string;
  /**
   * Composite `box-shadow` for widget cards. Stacks a soft drop
   * shadow with a thin accent-tinted ring at 1px so the card reads
   * as floating with a faint brand halo. Full CSS string.
   */
  widgetShadow: string;
  /**
   * Full `backdrop-filter` value applied to widget cards. Dark
   * variants run the original blur(16px) saturate(160%); light
   * variants drop to blur(8px) saturate(110%) because heavy blur
   * on a bright basemap reads as muddy fog.
   */
  widgetBlur: string;

  // ── Chrome (nav / drawer / popover / dense panel) ───────────────────
  // Higher-opacity sibling of the widget tier. Widget surfaces are
  // semi-transparent slabs floating over a map; chrome surfaces are
  // dense popovers/menus/panels that need to occlude the content they
  // sit on top of. Used by: TopNav dropdown panel, MobileNavOverlay
  // sheet, UserChipMenu popover, LocationMarker hover tooltip, and
  // LocationInfoCard side panel. The top-nav `.bar` itself stays on
  // the widget tier because its 0.7-alpha recipe is the slab shape.
  /**
   * Background colour for chrome surfaces (popovers, dropdowns, side
   * panels). Higher opacity than `widgetBg` so the content behind is
   * occluded rather than visible through the blur.
   */
  chromeBg: string;
  /**
   * Hairline border on chrome surfaces. Pairs with `chromeBg` per
   * (theme, mode); dark variants use white-at-low-alpha, light
   * variants use black-at-low-alpha, Luce variants pick a warm-toned
   * border.
   */
  chromeBorder: string;
  /**
   * Drop shadow for chrome surfaces. Heavier than the widget shadow
   * because chrome surfaces float above page chrome rather than
   * embedded in a card grid.
   */
  chromeShadow: string;
  /**
   * `backdrop-filter` value for chrome surfaces. Dark variants run
   * blur(16px) saturate(160%); light variants drop to blur(12px)
   * saturate(110%).
   */
  chromeBlur: string;
}

export interface ThemeRadii {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface ThemeFonts {
  sans: string;
  mono: string;
}

export interface ThemeMotion {
  fill: string;
  flow: string;
  pulse: string;
  press: string;
}

export interface ThemeTokens {
  colors: ThemeColors;
  radii: ThemeRadii;
  fonts: ThemeFonts;
  motion: ThemeMotion;
}

/** One half of a theme family — its light or dark variant. */
export interface ThemeVariant {
  mode: ModeId;
  tokens: ThemeTokens;
}

/** Theme family — owns a light variant and a dark variant. */
export interface Theme {
  id: ThemeFamilyId;
  name: string;
  description: string;
  /** Three colour swatches rendered in the picker row (bg / accent / text). */
  swatches: readonly [string, string, string];
  variants: {
    light: ThemeVariant;
    dark: ThemeVariant;
  };
  unlockedByDefault: boolean;
  costPoints: number;
  /**
   * Optional allow-list of the modes this family supports. Absent (the
   * default for every existing family) means BOTH modes are available
   * and the Light/Dark toggle shows both buttons. When set to a single
   * mode (e.g. ['dark']), `ThemeProvider` clamps the active mode to a
   * supported one before resolving the variant, and `ModeSegment`
   * renders only the supported button(s). Array order is the clamp
   * preference: the first entry is used when the saved mode isn't
   * supported.
   */
  availableModes?: ModeId[];
  /**
   * Optional per-theme Mapbox style URLs (one per mode). When set,
   * the PWA + admin map containers select the theme's style over
   * the satellite defaults. Omitted on themes that don't override
   * the map (Default + Luce keep the existing satellite styles).
   *
   * Values are full mapbox:// URLs so the contract stays loose:
   * a theme could ship a custom Mapbox Studio style without
   * needing a new enum case here.
   */
  mapStyle?: {
    light: string;
    dark: string;
  };
  /**
   * Driver Themes ticket — selects the charging-animation rendering
   * strategy for this family. Defaults to 'component' when omitted,
   * which routes through the per-theme React component map in the
   * ChargingAnimation dispatcher (Cartographic, Midnight, Sunset,
   * Bloom, Luce stay on this path). Set to 'shader' for the five
   * WebGL-rendered families (default / neon / electric-light /
   * dark-electric / obsidian), which routes through
   * ShaderChargingAnimation instead.
   */
  chargingAnimation?: 'shader' | 'component';
  /**
   * Driver Themes ticket — which shader program backs this family.
   * Required when chargingAnimation === 'shader'. Four programs
   * total: 'orb' is shared by Initiative + Electric Light + Dark
   * Electric (differentiated via shaderVariant); 'neon' and
   * 'obsidian' are standalone single-program shaders; 'aurora'
   * (Aurora theme — northern lights composition) is shared across
   * 'aurora-dark' + 'aurora-light' variants via the AURORA_VARIANT
   * compile-time define.
   */
  shaderKind?:
    | 'orb'
    | 'neon'
    | 'obsidian'
    | 'aurora'
    | 'cyber'
    | 'cloth'
    | 'physarum'
    | 'fluid';
  /**
   * Driver Themes ticket — variant selector for shaders that
   * compile per-variant programs. Injected as `#define ORB_KIND`
   * (orb shaders) or `#define AURORA_VARIANT` (aurora shaders) at
   * shader compile time to drive variant-specific source branches
   * + per-variant uniform-tuple selection in
   * ShaderChargingAnimation.tsx. Only meaningful for shader kinds
   * that compile per-variant (orb + aurora); obsidian + neon are
   * single-program shaders and ignore this field.
   */
  shaderVariant?:
    | 'initiative'
    | 'electric-light'
    | 'dark-electric'
    | 'aurora-dark'
    | 'aurora-light'
    | 'cyber-dark'
    | 'cyber-light'
    | 'cloth-dark'
    | 'cloth-light'
    // Interactive-sim variants (charging sims v2 — the REAL
    // simulations in SimChargingAnimation). 'fluid-initiative-*' is
    // mode-resolved by the dispatcher; 'fluid-marbling-light' is
    // Electric Light's single-mode marbling personality;
    // 'fluid-electric-dark' is Dark Electric's silver-on-black
    // reference-demo fluidity; 'physarum-dark' is the agent-network
    // sim (built and wired, currently unused — one registry flip
    // away for a future theme or skin).
    | 'fluid-initiative-dark'
    | 'fluid-initiative-light'
    | 'fluid-marbling-light'
    | 'fluid-electric-dark'
    | 'physarum-dark';
}

/** CSS custom-property names. One rename = one file edit. */
export const VAR = {
  bg: '--evi-bg',
  surface: '--evi-surface',
  surface2: '--evi-surface-2',

  sidebar: '--evi-sidebar',
  sidebarText: '--evi-sidebar-text',
  sidebarMute: '--evi-sidebar-mute',
  sidebarActive: '--evi-sidebar-active',
  navActive: '--evi-nav-active',
  navActiveBg: '--evi-nav-active-bg',

  border: '--evi-border',
  borderStrong: '--evi-border-strong',

  accent: '--evi-accent',
  accentSoft: '--evi-accent-soft',
  onAccent: '--evi-on-accent',

  charging: '--evi-charging',
  chargingGlow: '--evi-charging-glow',

  power: '--evi-power',
  info: '--evi-info',
  warn: '--evi-warn',
  good: '--evi-good',
  busy: '--evi-busy',

  mute: '--evi-mute',
  text: '--evi-text',
  textMute: '--evi-text-mute',

  cardGlow: '--evi-card-glow',
  cardGlowSubtle: '--evi-card-glow-subtle',

  widgetBg: '--evi-widget-bg',
  widgetBorder: '--evi-widget-border',
  widgetShadow: '--evi-widget-shadow',
  widgetBlur: '--evi-widget-blur',

  chromeBg: '--evi-chrome-bg',
  chromeBorder: '--evi-chrome-border',
  chromeShadow: '--evi-chrome-shadow',
  chromeBlur: '--evi-chrome-blur',

  radiusSm: '--evi-radius-sm',
  radiusMd: '--evi-radius-md',
  radiusLg: '--evi-radius-lg',
  radiusXl: '--evi-radius-xl',

  fontSans: '--evi-font-sans',
  fontMono: '--evi-font-mono',

  motionFill: '--evi-motion-fill',
  motionFlow: '--evi-motion-flow',
  motionPulse: '--evi-motion-pulse',
  motionPress: '--evi-motion-press',
} as const;

export function tokensToCssVars(t: ThemeTokens): Record<string, string> {
  return {
    [VAR.bg]: t.colors.bg,
    [VAR.surface]: t.colors.surface,
    [VAR.surface2]: t.colors.surface2,

    [VAR.sidebar]: t.colors.sidebar,
    [VAR.sidebarText]: t.colors.sidebarText,
    [VAR.sidebarMute]: t.colors.sidebarMute,
    [VAR.sidebarActive]: t.colors.sidebarActive,
    // Empty when unset: ThemeProvider removes the property, so another theme's value never lingers.
    [VAR.navActive]: t.colors.navActive ?? '',
    [VAR.navActiveBg]: t.colors.navActiveBg ?? '',

    [VAR.border]: t.colors.border,
    [VAR.borderStrong]: t.colors.borderStrong,

    [VAR.accent]: t.colors.accent,
    [VAR.accentSoft]: t.colors.accentSoft,
    [VAR.onAccent]: t.colors.onAccent,

    [VAR.charging]: t.colors.charging,
    [VAR.chargingGlow]: t.colors.chargingGlow,

    [VAR.power]: t.colors.power,
    [VAR.info]: t.colors.info,
    [VAR.warn]: t.colors.warn,
    [VAR.good]: t.colors.good,
    [VAR.busy]: t.colors.busy,

    [VAR.mute]: t.colors.mute,
    [VAR.text]: t.colors.text,
    [VAR.textMute]: t.colors.textMute,

    [VAR.cardGlow]: t.colors.cardGlow,
    [VAR.cardGlowSubtle]: t.colors.cardGlowSubtle,

    [VAR.widgetBg]: t.colors.widgetBg,
    [VAR.widgetBorder]: t.colors.widgetBorder,
    [VAR.widgetShadow]: t.colors.widgetShadow,
    [VAR.widgetBlur]: t.colors.widgetBlur,

    [VAR.chromeBg]: t.colors.chromeBg,
    [VAR.chromeBorder]: t.colors.chromeBorder,
    [VAR.chromeShadow]: t.colors.chromeShadow,
    [VAR.chromeBlur]: t.colors.chromeBlur,

    [VAR.radiusSm]: t.radii.sm,
    [VAR.radiusMd]: t.radii.md,
    [VAR.radiusLg]: t.radii.lg,
    [VAR.radiusXl]: t.radii.xl,

    [VAR.fontSans]: t.fonts.sans,
    [VAR.fontMono]: t.fonts.mono,

    [VAR.motionFill]: t.motion.fill,
    [VAR.motionFlow]: t.motion.flow,
    [VAR.motionPulse]: t.motion.pulse,
    [VAR.motionPress]: t.motion.press,
  };
}
